import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export const DEMO_TIMING = {
  stateA: 0,
  stateB: 1200,
  stateC: 2600,
  holdingStagger: 220,
  pnlWeekDelay: 180,
  cumReveal: 500,
} as const;

export const DEMO_STATUS = {
  a: "INPUTS · CASH + RISK",
  b: "BUILDING · READABLE BOOK",
  c: "OPEN P&L · SHOWN, NOT SPUN",
} as const;

export type DemoState = keyof typeof DEMO_STATUS;

export const CUM_START = "Cum. −0.0%";
export const CUM_END = "Cum. +0.4%";

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type PnlRef = RefObject<HTMLElement | null>;

export function useProductDemo(
  holdingCount: number,
  pnlRef?: PnlRef,
  cumEnd: string = CUM_END,
) {
  const [state, setState] = useState<DemoState>("a");
  const [visibleCount, setVisibleCount] = useState(0);
  const [week1Live, setWeek1Live] = useState(false);
  const [week2Live, setWeek2Live] = useState(false);
  const [cumText, setCumText] = useState(CUM_START);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  }, []);

  const pinPnl = useCallback(() => {
    const node = pnlRef?.current;
    if (!node) return;
    try {
      node.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    } catch {
      /* jsdom / older browsers */
    }
  }, [pnlRef]);

  const jumpToC = useCallback(() => {
    clearTimers();
    setState("c");
    setVisibleCount(holdingCount);
    setWeek1Live(true);
    setWeek2Live(true);
    setCumText(cumEnd);
    pinPnl();
  }, [clearTimers, cumEnd, holdingCount, pinPnl]);

  const play = useCallback(() => {
    if (prefersReducedMotion()) {
      jumpToC();
      return;
    }

    clearTimers();
    setState("a");
    setVisibleCount(0);
    setWeek1Live(false);
    setWeek2Live(false);
    setCumText(CUM_START);

    later(() => {
      setState("b");
      for (let i = 0; i < holdingCount; i += 1) {
        later(() => {
          setVisibleCount((n) => Math.max(n, i + 1));
        }, i * DEMO_TIMING.holdingStagger);
      }
    }, DEMO_TIMING.stateB);

    later(() => {
      setState("c");
      setVisibleCount(holdingCount);
      later(() => setWeek1Live(true), DEMO_TIMING.pnlWeekDelay);
      later(() => setWeek2Live(true), DEMO_TIMING.pnlWeekDelay * 2);
      later(() => setCumText(cumEnd), DEMO_TIMING.cumReveal);
      later(pinPnl, DEMO_TIMING.pnlWeekDelay);
    }, DEMO_TIMING.stateC);
  }, [clearTimers, cumEnd, holdingCount, jumpToC, later, pinPnl]);

  useEffect(() => {
    play();
    return clearTimers;
    // Autoplay once on mount, matching the prototype. Replay calls play().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    status: DEMO_STATUS[state],
    visibleCount,
    week1Live,
    week2Live,
    cumText,
    play,
    jumpToC,
  };
}
